import './AssessmentDetailsHeader.scss';

import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import { Box, Button, Grid, SpaceBetween, StatusIndicator, Toggle } from '@amzn/awsui-components-react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import AssessmentDetailsStatusBar from './AssessmentDetailsStatusBar';
import Paths from '../../../../Paths';
import { AppLabelsContextInterface, withAppLabelsContext } from '../../../../common/AppLabelsContext';
import Constants from '../../../../common/Constants';
import rumClient from '../../../../common/monitoring/RumClient';
import { setCurrentlyEditing } from '../../../administration/manage-templates/edit-template/CurrentTemplateSlice';
import { canEditLocale } from '../../../administration/manage-templates/edit-template/TemplatePermissionsUtils';
import { isTemplatePublished } from '../../../administration/manage-templates/edit-template/TemplateUtils';
import { useAppSelector } from '../../../redux/hooks';
import LiveSessionHeaderBar from '../../live-sessions/LiveSessionHeaderBar';

type AssessmentDetailsHeaderProps = AppLabelsContextInterface &
    AuthContextInterface & {
        readOnly: boolean;
        showLiveSession: boolean;
        showSnapshots: boolean;
        shouldDisplayTemplate: boolean;
    };

const AssessmentDetailsHeader: FunctionComponent<AssessmentDetailsHeaderProps> = ({
    appLabels,
    auth,
    showLiveSession,
    readOnly,
    shouldDisplayTemplate,
}): JSX.Element => {
    const assessmentId = useAppSelector((state) => state.currentAssessmentState.currentAssessmentId);
    const history = useHistory();
    const dispatch = useDispatch();

    // Template state
    const templateLocale = useAppSelector((state) => state.currentTemplateState.templateLocale);
    const templateMetadata = useAppSelector((state) => state.currentTemplateState.currentTemplateMetadata);
    const currentlyEditingTemplate = useAppSelector((state) => state.currentTemplateState.currentlyEditing);
    const editTemplateInProgressRequests = useAppSelector((state) => state.currentTemplateState.editTemplateInProgressRequests);
    const shouldShowEditTemplateError: boolean = useAppSelector((state) => state.currentTemplateState.editTemplateShowError);

    const liveSessionHeader = useCallback(() => {
        return showLiveSession ? <LiveSessionHeaderBar readOnly={readOnly} /> : null;
    }, [showLiveSession, readOnly]);

    const finalizeTemplateButton = useCallback(() => {
        return assessmentId === Constants.SAMPLE_ASSESSMENT_ID ? (
            <Button
                variant='primary'
                onClick={() => {
                    rumClient.recordClick(`${Paths.BASE_ASSESSMENTS_PATH}/${Constants.SAMPLE_ASSESSMENT_ID}/btn-finalize-template`);
                    history.push(Paths.FINALIZE_TEMPLATE_PATH);
                }}
                data-testid='btn-finalize-template'
            >
                {appLabels.manage_templates.actions.finalize_template}
            </Button>
        ) : null;
    }, [assessmentId, history, appLabels.manage_templates.actions.finalize_template]);

    const canEditTemplate = useMemo(() => {
        // Can edit if:
        // 1. We are displaying the template
        // 2. The template is not published
        // 3. The user has permission to edit the current locale
        return shouldDisplayTemplate && !isTemplatePublished(templateMetadata) && canEditLocale(templateLocale, templateMetadata, auth);
    }, [auth, shouldDisplayTemplate, templateLocale, templateMetadata]);

    const editTemplateToggle = useMemo(() => {
        return canEditTemplate ? (
            <Toggle checked={currentlyEditingTemplate} onChange={({ detail }) => dispatch(setCurrentlyEditing(detail.checked))}>
                {appLabels.manage_templates.edit.toggle}
            </Toggle>
        ) : null;
    }, [canEditTemplate, currentlyEditingTemplate, dispatch, appLabels.manage_templates.edit.toggle]);

    /**
     * A status indicator for editing a template
     */
    const editTemplateSaveStatus = useMemo(() => {
        if (!canEditTemplate) {
            return null;
        }

        if (shouldShowEditTemplateError) {
            return <StatusIndicator type='error'>{appLabels.statuses.failed_to_save_refresh}</StatusIndicator>;
        }

        if (editTemplateInProgressRequests?.size > 0) {
            return <StatusIndicator type='loading'>{appLabels.statuses.saving}</StatusIndicator>;
        } else {
            return <StatusIndicator type='success'>{appLabels.statuses.saved}</StatusIndicator>;
        }
    }, [editTemplateInProgressRequests, shouldShowEditTemplateError, canEditTemplate, appLabels.statuses]);

    return (
        <header className='assessment-header' data-testid='assessment-details-header'>
            <Box padding={{ left: 'm', right: 'm' }}>
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                    <div>
                        <AssessmentDetailsStatusBar shouldDisplayTemplate={shouldDisplayTemplate} />
                    </div>
                    <div className='assessment-details-header-right-column'>
                        <SpaceBetween direction='horizontal' size='m'>
                            {liveSessionHeader()}
                            {finalizeTemplateButton()}
                            {editTemplateSaveStatus}
                            {editTemplateToggle}
                        </SpaceBetween>
                    </div>
                </Grid>
            </Box>
        </header>
    );
};

export default withAuthContext(withAppLabelsContext(AssessmentDetailsHeader));
